import React from 'react'
import type { DashboardStatusCategory } from '@myapp/contract'
import { formatInt, formatPercent } from '../../../shared/lib/formatNumberDe'
import {
  DASHBOARD_CATEGORY_COLORS,
  DASHBOARD_CATEGORY_LABELS,
  type StatusSlice,
} from '../../../entities/dashboard'
import { useChartTooltip, TipRow, TipTitle } from '../model/useChartTooltip'

interface Props {
  slices: StatusSlice[]
  total: number
  selected: DashboardStatusCategory | null
  onToggle: (category: DashboardStatusCategory) => void
}

const RADIUS = 72
const STROKE = 30
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export const StatusDonut: React.FC<Props> = ({ slices, total, selected, onToggle }) => {
  const { bind, tooltip } = useChartTooltip()
  const visible = slices.filter((s) => s.count > 0)
  let offset = 0

  return (
    <div className="flex flex-wrap items-center justify-center gap-6">
      <svg viewBox="0 0 200 200" className="h-[200px] w-[200px] flex-none" role="img" aria-label="Aufträge nach Status">
        <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="#f3f4f6" strokeWidth={STROKE} />
        {slices.map((slice) => {
          const fraction = total > 0 ? slice.count / total : 0
          const length = Math.max(0, fraction * CIRCUMFERENCE - (visible.length > 1 && slice.count ? 3 : 0))
          const start = offset
          offset += fraction * CIRCUMFERENCE
          if (!slice.count) return null
          const dim = selected != null && selected !== slice.category
          return (
            <circle
              key={slice.category}
              cx="100"
              cy="100"
              r={RADIUS}
              fill="none"
              stroke={DASHBOARD_CATEGORY_COLORS[slice.category]}
              strokeWidth={STROKE}
              strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
              strokeDashoffset={-start}
              transform="rotate(-90 100 100)"
              opacity={dim ? 0.3 : 1}
              className="cursor-pointer transition-opacity"
              aria-hidden
              onClick={() => onToggle(slice.category)}
              {...bind(
                <>
                  <TipTitle>{DASHBOARD_CATEGORY_LABELS[slice.category]}</TipTitle>
                  <TipRow label="Aufträge" value={formatInt(slice.count)} />
                  <TipRow label="Anteil" value={formatPercent(slice.share)} />
                </>,
              )}
            />
          )
        })}
        <text x="100" y="102" textAnchor="middle" className="fill-[#124093] text-[36px] font-bold">
          {formatInt(total)}
        </text>
        <text x="100" y="122" textAnchor="middle" className="fill-gray-500 font-['Roboto'] text-[13px]">
          Aufträge
        </text>
      </svg>

      <div className="flex min-w-0 flex-[1_1_230px] flex-col gap-1">
        {slices.map((slice) => {
          const on = selected === slice.category
          const dim = selected != null && !on
          return (
            <button
              key={slice.category}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(slice.category)}
              className={`grid grid-cols-[12px_minmax(0,1fr)_auto_52px] items-center gap-2.5 rounded-[10px] px-2 py-1.5 text-left font-['Roboto'] text-sm text-gray-800 hover:bg-[#F0F6FF] ${on ? 'bg-[#E6F0FB]' : ''} ${dim ? 'opacity-45' : ''}`}
            >
              <i aria-hidden className="h-3 w-3 rounded-full" style={{ background: DASHBOARD_CATEGORY_COLORS[slice.category] }} />
              <span>{DASHBOARD_CATEGORY_LABELS[slice.category]}</span>
              <b className="font-bold tabular-nums text-[#124093]">{slice.count}</b>
              <span className="text-right tabular-nums text-[#6b7a94]">{formatPercent(slice.share)}</span>
            </button>
          )
        })}
      </div>
      {tooltip}
    </div>
  )
}
